import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { uniq } from 'lodash';
import { BringgInput, Divider, PopOver, Tooltip, Spacing, DataNode } from '@bringg/react-components';
// TODO: fix react-components <Tree treeData={treeData}/> is not reactive to treeData changes
// eslint-disable-next-line no-restricted-imports
import { Tree, TreeProps, TreeNodeProps } from 'antd';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useTranslation } from 'react-i18next';

import styles from './csv-import-columns-popover.module.scss';

function isDefined<T>(value: T): value is NonNullable<T> {
	return value !== null && value !== undefined;
}

export type TranslationKeys = {
	originalField: string;
	search: string;
};

export type ImportColumnsDataNode = {
	translationTitleKey: string;
	key: string;
	children?: {
		translationTitleKey: string;
		key: string;
		disabled?: boolean;
	}[];
};

export type Props = {
	children: ReactNode;
	originalTitle: string;
	onSelect: NonNullable<TreeProps['onSelect']>;
	selected: string | null;
	headers: (string | null)[];
	translations: TranslationKeys;
	treeData: ImportColumnsDataNode[];
	columnsByDataIndex: Record<string, { translationTitleKey: string }>;
	initiallyExpanded?: string[];
	readonly?: boolean;
};

const findExpandedByDataIndex = (treeData: ImportColumnsDataNode[], dataIndex: string | null): string[] => {
	if (!dataIndex) {
		return [];
	}

	return [treeData.find(({ children }) => children?.find(({ key }) => key === dataIndex))?.key].filter(isDefined);
};

export default function CsvImportColumnsPopover({
	children,
	originalTitle,
	onSelect,
	selected,
	headers,
	treeData,
	columnsByDataIndex,
	translations,
	initiallyExpanded = [],
	readonly = false
}: Props) {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');
	const [expandedKeys, setExpandedKeys] = useState<string[]>(findExpandedByDataIndex(treeData, selected));

	useEffect(() => {
		setExpandedKeys(prev => uniq([...prev, ...initiallyExpanded]));
	}, []);

	const handleSelect = useCallback<NonNullable<TreeProps['onSelect']>>(
		(_, e) => {
			if (e.node.children) {
				const selectedKey = e.node.key.toString();

				setExpandedKeys(prev => {
					if (prev.includes(selectedKey)) {
						return prev.filter(key => key !== selectedKey);
					}
					return [...prev, selectedKey];
				});
			} else {
				onSelect(_, e);
			}
		},
		[onSelect]
	);

	// TODO: rewrite to separate react component
	const treeDataWithTooltip = useMemo<DataNode[]>(() => {
		const disableIfSelected = (node: ImportColumnsDataNode) => ({
			...node,
			children: node.children?.map(child => ({
				...child,
				disabled: headers.includes(child.key.toString()) && child.key !== selected
			}))
		});

		const addStyles = (node: ImportColumnsDataNode): DataNode => {
			const childNode = (translationKey: string) => (
				<div className={styles.csvImportColumnsNode}>
					<span>{t(translationKey)}</span>
					<BringgIcon iconName={BringgFontIcons.Close} />
				</div>
			);

			return {
				...node,
				title: <div className={styles.csvImportColumnsNodeParent}>{t(node.translationTitleKey)}</div>,
				children: node.children?.map(child => ({
					...child,
					title: !child.disabled ? (
						<Tooltip
							overlayClassName={styles.csvImportColumnsTooltip}
							title={t(`${child.translationTitleKey}_TOOLTIP`)}
							placement="right"
						>
							{childNode(child.translationTitleKey)}
						</Tooltip>
					) : (
						childNode(child.translationTitleKey)
					)
				}))
			};
		};

		if (!search) {
			return treeData.map(node => addStyles(disableIfSelected(node)));
		}

		function filter(node: ImportColumnsDataNode) {
			return (
				t(node.translationTitleKey).toLowerCase().includes(search.trim().toLowerCase()) ||
				node.children?.some(filter)
			);
		}

		return treeData
			.map(node => {
				if (!filter(node)) {
					return null;
				}

				return addStyles(
					disableIfSelected({
						...node,
						children: node.children?.filter(filter)
					})
				);
			})
			.filter(isDefined);
	}, [search, treeData, headers, selected]);

	useEffect(() => {
		if (treeDataWithTooltip.length > 0 && treeDataWithTooltip.length < 3) {
			setExpandedKeys(treeDataWithTooltip.map(node => node?.key.toString() || ''));
		}
	}, [treeDataWithTooltip]);

	const switcherIcon: TreeProps['switcherIcon'] = (switcherProps: TreeNodeProps) =>
		switcherProps.expanded ? (
			<BringgIcon iconName={BringgFontIcons.Chevron} />
		) : (
			<BringgIcon iconName={BringgFontIcons.ChevronRight} />
		);

	const showTitle = (node: ReactNode) => {
		if (typeof node !== 'string') {
			return node || originalTitle;
		}

		const translationKey = columnsByDataIndex[node]?.translationTitleKey;

		return translationKey ? t(translationKey) : originalTitle;
	};

	return (
		<PopOver
			destroyTooltipOnHide
			placement="bottom"
			trigger={readonly ? [] : ['click']}
			content={
				<div className={styles.csvImportColumnPopover}>
					<div className={styles.originalField}>{translations.originalField}</div>
					<div className={styles.originalTitle}>{originalTitle}</div>
					<Divider spacing={9 as Spacing} />
					<BringgInput
						autoFocus
						style={{ margin: '16px 0' }}
						value={search}
						onChange={e => setSearch(e.target.value)}
						allowClear
						placeholder={translations.search}
						type="search"
						prefix={<BringgIcon className={styles.searchIcon} iconName={BringgFontIcons.Search} />}
					/>
					<Divider spacing={9 as Spacing} />
					<Tree
						switcherIcon={switcherIcon}
						showLine
						autoExpandParent
						expandedKeys={expandedKeys}
						onExpand={keys => setExpandedKeys(keys.map(key => key.toString()))}
						onSelect={handleSelect}
						selectedKeys={selected ? [selected] : undefined}
						height={300}
						treeData={treeDataWithTooltip}
					/>
				</div>
			}
		>
			<div style={{ whiteSpace: 'nowrap' }}>
				{showTitle(children)}
				{!readonly && <BringgIcon iconName={BringgFontIcons.Chevron} />}
			</div>
		</PopOver>
	);
}
